import { Button } from '@up2stay/up2promo-ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { captureException } from '@/core/posthog-init';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Route-level error boundary that wraps <Outlet> inside SetupLayout.
 *
 * When a route-transition crash occurs (e.g. React removeChild during
 * navigation away from AddressPage / CustomUrlPage), this boundary catches it
 * and renders a compact inline recovery UI instead of wiping the entire app.
 *
 * "Try again" resets the boundary state, letting React re-render the current
 * route. Zustand in-memory state is unaffected by the boundary reset, so the
 * user's form data is preserved and they can resubmit without starting over.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, {
      errorBoundary: 'route',
      componentStack: info.componentStack ?? undefined,
    });
  }

  reset = () => {
    this.setState({ hasError: false });
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-base font-semibold text-foreground">
            Something went wrong on this step
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Reload page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
